import { useEffect, useMemo, useRef, useState } from "react";
import Lottie, {
    LottieRefCurrentProps,
} from "lottie-react";
import MobileFrame from "../../../components/MobileFrame";

import {
    CalendarClock,
    Loader2,
} from "lucide-react";

import {
    getJourneyByOrderId,
} from "../../../api/journey.api";

import {
    initiateDownPayment,
    getDownPaymentTransactionStatus,
} from "../../../api/downpayment.api";

import successAnimation from "../../../assets/Success.json";
import failedAnimation from "../../../assets/failure.json";
import { ApiError } from "../../../api/ApiError";


interface JourneyResponse {
    orderId?: string;
    orderAmount?: number;
    dpAmount?: number;
    customerName?: string;
    customerEmail?: string;
    productDetails?: string;
    emiAmount?: number;
    callbackUrl?: string;
    emiStartDate?: string;
}


interface TsofflineProps {
    redirectSeconds?: number;
}


type PaymentResult =
    | "SUCCESS"
    | "FAILED"
    | "PROCESSING";


type FailureType =
    | "FAILED"
    | "CANCELLED"
    | "TIMEOUT";


const Tsoffline = ({
    redirectSeconds = 10,
}: TsofflineProps) => {

    /*
     * ============================================================
     * URL
     * ============================================================
     *
     * The URL contains ONLY:
     *
     * orderId
     * transactionId
     *
     * Payment status is NEVER trusted from the URL.
     *
     * Example:
     *
     * /transactionstatusoffline
     * ?orderId=CP6804796810
     * &transactionId=CP6804796810_57426360_1047
     */


    const searchParams = useMemo(
        () =>
            new URLSearchParams(
                window.location.search
            ),
        []
    );


    const orderId =
        searchParams.get("orderId") ||
        searchParams.get("order_id") ||
        searchParams.get("ORDERID") ||
        "";


    const transactionId =
        searchParams.get("transactionId") || "";


    /*
     * ============================================================
     * STATE
     * ============================================================
     */


    const [journey, setJourney] =
        useState<JourneyResponse | null>(null);


    const [paymentResult, setPaymentResult] =
        useState<PaymentResult | null>(null);


    const [paymentMessage, setPaymentMessage] =
        useState<string | null>(null);


    const [canRetry, setCanRetry] =
        useState(false);


    const [journeyLoading, setJourneyLoading] =
        useState(true);


    const [statusLoading, setStatusLoading] =
        useState(true);


    const [apiError, setApiError] =
        useState<string | null>(null);


    const [secondsLeft, setSecondsLeft] =
        useState(redirectSeconds);


    const [isRetrying, setIsRetrying] =
        useState(false);


    const successLottieRef =
        useRef<LottieRefCurrentProps>(null);


    const failedLottieRef =
        useRef<LottieRefCurrentProps>(null);


    const loading =
        journeyLoading ||
        statusLoading;


    /*
     * ============================================================
     * GET JOURNEY
     * ============================================================
     *
     * Used ONLY for displaying transaction details.
     *
     * It does NOT determine payment status.
     */


    useEffect(() => {

        if (!orderId) {

            setApiError(
                "Order ID is missing"
            );

            setJourneyLoading(false);

            return;
        }


        const fetchJourney = async () => {

            try {

                setJourneyLoading(true);
                setApiError(null);


                const response =
                    await getJourneyByOrderId(
                        orderId
                    );


                setJourney(
                    response.data
                );

            } catch (error) {

                console.error(
                    "[Tsoffline] Error fetching journey:",
                    error
                );


                setApiError(
                    "Unable to fetch transaction details"
                );

            } finally {

                setJourneyLoading(false);

            }
        };


        void fetchJourney();

    }, [orderId]);


    /*
     * ============================================================
     * GET TRANSACTION STATUS
     * ============================================================
     *
     * Backend is the source of truth.
     *
     * PROCESSING:
     *     Poll every 2 seconds.
     *
     * SUCCESS:
     *     Stop polling.
     *
     * FAILED:
     *     Stop polling.
     */


    useEffect(() => {

        if (!orderId) {

            setStatusLoading(false);

            return;
        }


        if (!transactionId) {

            setApiError(
                "Transaction ID is missing"
            );

            setStatusLoading(false);

            return;
        }


        let cancelled = false;

        let timeoutId:
            ReturnType<typeof setTimeout>;


        const MAX_ATTEMPTS = 10;
        const POLL_INTERVAL = 2000;


        const checkStatus = async (
            attempt: number
        ): Promise<void> => {

            try {

                const response =
                    await getDownPaymentTransactionStatus(
                        orderId,
                        transactionId
                    );


                if (cancelled) {
                    return;
                }


                setPaymentResult(
                    response.transactionStatus
                );


                setPaymentMessage(
                    response.message ?? null
                );


                setCanRetry(
                    response.canRetry
                );


                /*
                 * =================================================
                 * TERMINAL STATES
                 * =================================================
                 */

                if (
                    response.transactionStatus ===
                    "SUCCESS"
                ) {

                    setStatusLoading(false);

                    return;
                }


                if (
                    response.transactionStatus ===
                    "FAILED"
                ) {

                    setStatusLoading(false);

                    return;
                }


                /*
                 * =================================================
                 * STILL PROCESSING
                 * =================================================
                 */

                if (
                    attempt < MAX_ATTEMPTS
                ) {

                    timeoutId =
                        setTimeout(
                            () => {
                                void checkStatus(
                                    attempt + 1
                                );
                            },
                            POLL_INTERVAL
                        );

                    return;
                }


                /*
                 * =================================================
                 * MAX POLLING ATTEMPTS REACHED
                 * =================================================
                 *
                 * Keep the user on PROCESSING screen.
                 *
                 * We do NOT mark it as failed just because
                 * polling timed out.
                 */

                setPaymentResult(
                    "PROCESSING"
                );

                setStatusLoading(false);

            } catch (error) {

                if (cancelled) {
                    return;
                }

                let message =
                    "Unable to fetch transaction status.";

                if (error instanceof ApiError) {

                    if (
                        error.status >= 500 &&
                        error.status < 600
                    ) {
                        message =
                            "Something went wrong. Please try again later.";
                    } else {
                        message =
                            error.message?.trim() ||
                            message;
                    }
                }

                setApiError(message);
                setStatusLoading(false);
            }
        };


        void checkStatus(1);


        return () => {

            cancelled = true;


            if (timeoutId) {
                clearTimeout(timeoutId);
            }

        };

    }, [
        orderId,
        transactionId,
    ]);


    /*
     * ============================================================
     * AMOUNTS
     * ============================================================
     */


    const orderAmount =
        Number(
            journey?.orderAmount ?? 0
        );


    const downPayment =
        Number(
            journey?.dpAmount ?? 0
        );


    const loanAmount =
        Math.max(
            0,
            orderAmount - downPayment
        );


    const productDetails =
        journey?.productDetails ??
        "Product details unavailable";


    const emiAmount =
        Number(
            journey?.emiAmount ?? 0
        );


    const callbackUrl =
        journey?.callbackUrl ?? "";


    /*
     * ============================================================
     * CUSTOMER NAME
     * ============================================================
     */


    const customerName =
        journey?.customerName
            ?.trim()
            .split(/\s+/)[0] ?? "";


    /*
     * ============================================================
     * FORMAT CURRENCY
     * ============================================================
     */


    const formatCurrency = (
        amount: number
    ) => {

        return `₹${amount.toLocaleString(
            "en-IN"
        )}`;

    };


    /*
  * ============================================================
  * FIRST EMI DATE
  * ============================================================
  */

    const firstEmiDueDate =
        journey?.emiStartDate
            ? new Date(
                journey.emiStartDate
            ).toLocaleDateString(
                "en-IN",
                {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                }
            )
            : "";


    /*
     * ============================================================
     * FAILURE MESSAGE CLASSIFICATION
     * ============================================================
     *
     * Easebuzz can return messages such as:
     *
     * "Transaction has failed."
     *
     * "Merchant has canceled the transaction from
     *  the bank page."
     *
     * "Session expired for the transaction."
     *
     * We classify the message only for UI wording.
     *
     * The actual payment status still comes from backend.
     */


    const getFailureType = (
        message: string | null
    ): FailureType => {

        const normalizedMessage =
            message?.toLowerCase() ?? "";


        if (
            normalizedMessage.includes(
                "cancel"
            )
        ) {

            return "CANCELLED";

        }


        if (
            normalizedMessage.includes(
                "expired"
            ) ||
            normalizedMessage.includes(
                "timeout"
            ) ||
            normalizedMessage.includes(
                "timed out"
            )
        ) {

            return "TIMEOUT";

        }


        return "FAILED";

    };


    const failureType =
        getFailureType(
            paymentMessage
        );


    /*
     * ============================================================
     * FAILURE UI TEXT
     * ============================================================
     */


    const failureTitle =
        failureType === "CANCELLED"
            ? "Down Payment Cancelled"
            : failureType === "TIMEOUT"
                ? "Down Payment Timeout"
                : "Down Payment Failed";


    const failureDescription =
        failureType === "CANCELLED"
            ? "The down payment was cancelled."
            : failureType === "TIMEOUT"
                ? "The payment session expired before the transaction could be completed."
                : "Your down payment wasn’t successful.";


    /*
     * ============================================================
     * REDIRECT COUNTDOWN
     * ============================================================
     *
     * SUCCESS:
     *     Always redirect.
     *
     * PUBLIC FAILED:
     *     canRetry = false
     *     Therefore redirect automatically.
     *
     * INTERNAL FAILED:
     *     canRetry = true
     *     Stay on failure screen and show Try Again.
     */


    const shouldAutoRedirect =
        paymentResult === "SUCCESS" ||
        (
            paymentResult === "FAILED" &&
            !canRetry
        );


    useEffect(() => {

        if (!shouldAutoRedirect) {
            return;
        }


        if (secondsLeft <= 0) {

            if (callbackUrl) {

                window.location.href =
                    callbackUrl;

            }

            return;

        }


        const timer =
            setTimeout(() => {

                setSecondsLeft(
                    (prev) =>
                        prev - 1
                );

            }, 1000);


        return () =>
            clearTimeout(timer);

    }, [
        shouldAutoRedirect,
        secondsLeft,
        callbackUrl,
    ]);


    /*
     * ============================================================
     * REDIRECT PROGRESS
     * ============================================================
     */


    const redirectProgress =
        redirectSeconds > 0
            ? (
                (
                    redirectSeconds -
                    secondsLeft
                ) /
                redirectSeconds
            ) * 100
            : 0;


    /*
     * ============================================================
     * RETRY PAYMENT
     * ============================================================
     *
     * This is INTERNAL flow only.
     *
     * PUBLIC failures have canRetry=false,
     * so this button is never shown.
     */


    const handleTryAgain = async () => {

        if (
            isRetrying ||
            !journey ||
            !canRetry
        ) {

            return;

        }


        try {

            setIsRetrying(true);
            setApiError(null);


            const retryPayload = {

                orderId:
                    journey.orderId ??
                    orderId,

                email:
                    journey.customerEmail ??
                    "",

                firstname:
                    journey.customerName ??
                    "",

                amount:
                    journey.dpAmount ?? 0,

            };


            const response =
                await initiateDownPayment(
                    retryPayload
                );


            if (
                !response.success ||
                !response.paymentLink
            ) {

                throw new Error(
                    "Payment link was not generated."
                );

            }


            window.location.href =
                response.paymentLink;

        } catch (error) {

            let message =
                "Unable to initiate payment. Please try again.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setApiError(message);

            setIsRetrying(false);

        }

    };


    /*
     * ============================================================
     * LOADING SCREEN
     * ============================================================
     */


    if (loading) {

        return (

            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >

                <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                    <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                    <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />


                    <div className="relative z-10 flex flex-col items-center justify-center h-full px-6">

                        <div className="h-20 w-20 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />


                        <p className="mt-5 text-[15px] font-semibold text-gray-900">

                            Processing your transaction

                        </p>


                        <p className="mt-1 text-[11px] text-gray-500">

                            Please wait...

                        </p>

                    </div>

                </div>

            </MobileFrame>

        );

    }


    /*
     * ============================================================
     * PROCESSING SCREEN
     * ============================================================
     *
     * This means:
     *
     * - Backend has not received final transaction state yet
     * - Or polling window ended
     *
     * We do NOT show failure.
     */


    if (
        paymentResult ===
        "PROCESSING"
    ) {

        return (

            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >

                <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                    <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                    <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />


                    <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">


                        {/* HEADER */}

                        <div className="flex items-center justify-end shrink-0">

                            <img
                                src="/CleverPeLogos/CleverPE_Black_icon.svg"
                                alt="CleverPe"
                                className="h-6 w-6 opacity-90"
                            />

                        </div>


                        {/* PROCESSING */}

                        <div className="flex-1 flex flex-col items-center justify-center text-center">

                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-indigo-50">

                                <Loader2 className="h-9 w-9 animate-spin text-indigo-500" />

                            </div>


                            <p className="mt-5 text-[21px] font-extrabold text-gray-900 leading-tight">

                                Processing your transaction

                            </p>


                            <p className="mt-2 max-w-[280px] text-[13px] font-medium leading-relaxed text-gray-600">

                                We are confirming your payment.
                                Please wait while we verify the transaction.

                            </p>


                            {orderId && (

                                <div className="mt-4 flex items-center justify-center">

                                    <span className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-medium text-slate-500 shadow-sm">

                                        Order ID:{" "}

                                        <span className="font-semibold text-slate-800">

                                            {orderId}

                                        </span>

                                    </span>

                                </div>

                            )}

                        </div>


                        <p className="mb-1 mt-4 text-center text-[11px] font-semibold tracking-wide text-gray-400 shrink-0">

                            CleverPe

                        </p>

                    </div>

                </div>

            </MobileFrame>

        );

    }


    /*
     * ============================================================
     * FAILURE SCREEN
     * ============================================================
     */


    if (
        paymentResult ===
        "FAILED"
    ) {

        return (

            <MobileFrame
                glowColor="#c7d2fe"
                borderColor="#e5e7eb"
                width={420}
            >

                <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                    <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                    <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />


                    <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">


                        {/* HEADER */}

                        <div className="flex items-center justify-end shrink-0">

                            <img
                                src="/CleverPeLogos/CleverPE_Black_icon.svg"
                                alt="CleverPe"
                                className="h-6 w-6 opacity-90"
                            />

                        </div>


                        {/* FAILURE */}

                        <div className="flex-1 flex flex-col items-center justify-center text-center">

                            <div className="h-40 w-40 -mt-4">

                                <Lottie
                                    lottieRef={
                                        failedLottieRef
                                    }
                                    animationData={
                                        failedAnimation
                                    }
                                    loop={false}
                                    autoplay
                                    onComplete={() => {

                                        failedLottieRef.current?.goToAndStop(
                                            90,
                                            true
                                        );

                                    }}
                                />

                            </div>


                            <p className="mt-4 text-[21px] font-extrabold text-gray-900 leading-tight">

                                {failureTitle}

                            </p>


                            <p className="mt-2 text-[14px] font-medium text-gray-600">

                                {failureDescription}

                            </p>


                            {/* EXACT EASEBUZZ MESSAGE */}

                            {paymentMessage && (

                                <div className="mt-3 max-w-[300px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">

                                    <p className="text-[10px] font-medium leading-relaxed text-slate-500">

                                        {paymentMessage}

                                    </p>

                                </div>

                            )}


                            {orderId && (

                                <div className="mt-4 flex items-center justify-center">

                                    <span className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-medium text-slate-500 shadow-sm">

                                        Order ID:{" "}

                                        <span className="font-semibold text-slate-800">

                                            {orderId}

                                        </span>

                                    </span>

                                </div>

                            )}

                        </div>


                        {/* =================================================
                         * INTERNAL FAILED
                         * ================================================= */}

                        {canRetry && (

                            <div className="mt-auto px-2.5">


                                {/* RETRY ERROR */}

                                {apiError && (
                                    <div
                                        role="alert"
                                        className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-center"
                                    >
                                        <p className="text-[11px] font-semibold text-red-700">
                                            {apiError}
                                        </p>
                                    </div>
                                )}


                                <button
                                    type="button"
                                    onClick={
                                        handleTryAgain
                                    }
                                    disabled={
                                        isRetrying
                                    }
                                    className="
                                        w-full
                                        py-3
                                        rounded-full
                                        bg-gradient-to-r
                                        from-indigo-500
                                        to-blue-400
                                        text-[13px]
                                        font-semibold
                                        text-white
                                        shadow-sm
                                        flex
                                        items-center
                                        justify-center
                                        gap-2
                                        disabled:opacity-60
                                        active:scale-[0.98]
                                        transition
                                    "
                                >

                                    {isRetrying && (

                                        <Loader2
                                            className="
                                                h-4
                                                w-4
                                                animate-spin
                                            "
                                        />

                                    )}


                                    {isRetrying
                                        ? "Processing Payment..."
                                        : "Try Again"
                                    }

                                </button>

                            </div>

                        )}


                        {/* =================================================
                         * PUBLIC FAILED
                         * =================================================
                         *
                         * canRetry=false means:
                         *
                         * No retry button.
                         * Automatically redirect after 10 seconds.
                         * Same behaviour as success.
                         * ================================================= */}

                        {!canRetry && (

                            <div className="mt-auto p-2.5">

                                <div className="flex items-center justify-between">

                                    <p className="text-[10.5px] font-medium text-indigo-700">

                                        Redirecting in

                                    </p>


                                    <span className="text-[12px] font-bold text-indigo-700 tabular-nums">

                                        {secondsLeft}s

                                    </span>

                                </div>


                                <div className="mt-1.5 h-1 w-full rounded-full bg-indigo-100 overflow-hidden">

                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400"
                                        style={{
                                            width:
                                                `${redirectProgress}%`,
                                            transition:
                                                "width 1s linear",
                                        }}
                                    />

                                </div>

                            </div>

                        )}


                        {/* FOOTER */}

                        <p className="mb-1 mt-4 text-center text-[11px] font-semibold tracking-wide text-gray-400 shrink-0">

                            CleverPe

                        </p>

                    </div>

                </div>

            </MobileFrame>

        );

    }


    /*
     * ============================================================
     * SUCCESS SCREEN
     * ============================================================
     */


    return (

        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >

            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />


                <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">


                    {/* HEADER */}

                    <div className="flex items-center justify-between shrink-0">

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="ml-auto h-6 w-6 opacity-90"
                        />

                    </div>


                    {/* SUCCESS */}

                    <div className="relative -mt-7 flex flex-col items-center text-center shrink-0">

                        <div className="h-48 w-48">

                            <Lottie
                                lottieRef={
                                    successLottieRef
                                }
                                animationData={
                                    successAnimation
                                }
                                loop={false}
                                autoplay
                                onComplete={() => {

                                    successLottieRef.current?.goToAndStop(
                                        90,
                                        true
                                    );

                                }}
                            />

                        </div>


                        <p className="mt-1 text-[25px] font-extrabold text-gray-900 leading-tight">

                            Congratulations{" "}

                            {customerName}!

                        </p>


                        <p className="mt-1 text-[14px] font-medium text-green-600">

                            Your transaction has been successfully completed

                        </p>

                    </div>


                    {/* SUCCESS BODY */}

                    <div className="relative mt-4 flex-1 min-h-0 flex flex-col gap-2 overflow-hidden">


                        {/* ORDER ID */}

                        <div className="flex items-center justify-center">

                            <span className="whitespace-nowrap rounded-full border border-slate-200 bg-white px-3 py-1 text-[10px] font-medium text-slate-500 shadow-sm">

                                Order ID:{" "}

                                <span className="font-semibold text-slate-800">

                                    {journey?.orderId ??
                                        orderId}

                                </span>

                            </span>

                        </div>


                        {/* LOAN SUMMARY */}

                        <div className="mt-1 rounded-xl border border-slate-100 bg-white p-2.5 shadow-sm">

                            <p className="mb-1.5 text-[12px] font-bold leading-tight text-gray-900">

                                Loan Summary

                            </p>


                            <div className="grid grid-cols-[1fr_2fr] border-y border-slate-100">


                                {/* LOAN AMOUNT */}

                                <div className="flex min-h-[58px] flex-col justify-center px-3 py-2">

                                    <p className="text-[9px] font-medium text-gray-400">

                                        Loan Amount

                                    </p>


                                    <p className="mt-1 text-[12px] font-bold text-gray-900">

                                        {formatCurrency(
                                            loanAmount
                                        )}

                                    </p>

                                </div>


                                {/* PRODUCT INFO */}

                                <div className="flex min-h-[58px] flex-col justify-center border-l border-slate-100 px-3 py-2">

                                    <p className="text-[9px] font-medium text-gray-400">

                                        Product Info

                                    </p>


                                    <p className="mt-1 line-clamp-2 text-[12px] font-bold leading-4 text-gray-900">

                                        {productDetails}

                                    </p>

                                </div>

                            </div>

                        </div>


                        {/* EMI REMINDER */}

                        <div className="mt-1 relative overflow-hidden flex items-start gap-2.5 rounded-2xl border border-amber-200/70 bg-gradient-to-br from-amber-50 to-orange-50 px-3.5 py-3">

                            <div className="absolute -right-4 -top-4 h-16 w-16 rounded-full bg-amber-200/20 blur-2xl" />


                            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white text-amber-600 shadow-sm ring-1 ring-amber-100">

                                <CalendarClock className="h-4 w-4" />

                            </div>


                            <div className="relative">

                                <p className="text-[11.5px] font-bold text-amber-900 leading-tight">

                                    Keep your wallet funded!

                                </p>


                                <p className="mt-0.5 text-[10px] leading-snug text-amber-700">

                                    EMI of{" "}

                                    <span className="font-bold text-amber-800">

                                        {formatCurrency(
                                            emiAmount
                                        )}

                                    </span>{" "}

                                    will be auto-debited monthly

                                </p>


                                {firstEmiDueDate && (

                                    <div className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-white/70 px-2 py-0.5 ring-1 ring-amber-200/70">

                                        <span className="text-[9px] font-medium text-amber-700">

                                            1st EMI due

                                        </span>


                                        <span className="text-[9px] font-bold text-amber-900">

                                            {firstEmiDueDate}

                                        </span>

                                    </div>

                                )}

                            </div>

                        </div>


                        {/* SUCCESS REDIRECT */}

                        <div className="mt-auto p-2.5">

                            <div className="flex items-center justify-between">

                                <p className="text-[10.5px] font-medium text-indigo-700">

                                    Redirecting in

                                </p>


                                <span className="text-[12px] font-bold text-indigo-700 tabular-nums">

                                    {secondsLeft}s

                                </span>

                            </div>


                            <div className="mt-1.5 h-1 w-full rounded-full bg-indigo-100 overflow-hidden">

                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-blue-400"
                                    style={{
                                        width:
                                            `${redirectProgress}%`,
                                        transition:
                                            "width 1s linear",
                                    }}
                                />

                            </div>

                        </div>

                    </div>


                    {/* FOOTER */}

                    <p className="mb-1 mt-2 text-center text-[11px] font-semibold tracking-wide text-gray-400 shrink-0">

                        CleverPe

                    </p>

                </div>

            </div>

        </MobileFrame>

    );

};


export default Tsoffline;